/// <amd-module name="ExtensionManagement/ManagementHub/RequestDetailsTable" />
import * as React from "react";

import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";

import { IVssPageContext } from "VSS/Platform/Context";

import { Ago } from "VSSUI/Ago";
import { ISimpleListCell } from "VSSUI/List";
import { Table, ITableColumn, SimpleTableCell, renderSimpleCellValue, renderSimpleCell } from "VSSUI/Table";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IPersonaProvider, Persona, PersonaSize } from "VSSUI/Persona";

import { ExtensionRequestState } from "./Contracts/ExtensionManagement";
import { IExtensionActionViewModel, IRequestedExtensionViewModel } from "./UIContracts";

import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";

interface IRequestDetailTableProps {
    /**
     * Request whose details to be shown
     */
    extension: IRequestedExtensionViewModel;

    /**
     * Context for Persona provider.
     */
    pageContext: IVssPageContext;
}

/**
 * Dialog to view details of request which consists of
 * all requests from current and other users for this extension
 * and if extension is resolved then resolutions from admin.
 */
export class RequestDetailsTable extends React.Component<IRequestDetailTableProps> {
    private personaProvider: IPersonaProvider;
    private requestersColumns: ITableColumn<IExtensionActionViewModel | ISimpleListCell>[];

    constructor(props: IRequestDetailTableProps) {
        super(props);

        this.requestersColumns = [
            {
                id: "requester",
                name: Resources.RequestedBy,
                width: 400,
                renderCell: this.renderUserNameColumn
            },
            {
                id: "requestedDate",
                name: Resources.RequestedOn,
                width: 200,
                renderCell: this.renderActionTimeColumn
            },
            { id: "message", name: Resources.Message, width: -100, minWidth: 300, renderCell: this.renderUserMessage }
        ];

        this.personaProvider = new PersonaDetailsProvider(this.props.pageContext);
    }

    public render(): JSX.Element {
        let resolver: JSX.Element | undefined = undefined;

        if (this.props.extension.resolution) {
            const resolverColumns: Array<ITableColumn<IExtensionActionViewModel | ISimpleListCell>> = [
                {
                    id: "resolver",
                    name: this.props.extension.requestState === ExtensionRequestState.Accepted ? Resources.ApprovedBy : Resources.RejectedBy,
                    width: 400,
                    renderCell: this.renderUserNameColumn
                },
                {
                    id: "resolverDate",
                    name: this.props.extension.requestState === ExtensionRequestState.Accepted ? Resources.ApprovedOn : Resources.RejectedOn,
                    width: 200,
                    renderCell: this.renderActionTimeColumn
                },
                { id: "message", name: Resources.Message, width: -100, minWidth: 300, renderCell: this.renderUserMessage }
            ];

            resolver = (
                <Table<IExtensionActionViewModel>
                    columns={resolverColumns}
                    itemProvider={new ArrayItemProvider<IExtensionActionViewModel>([this.props.extension.resolution])}
                    singleClickActivation={false}
                />
            );
        }

        return (
            <div>
                <Table<IExtensionActionViewModel>
                    columns={this.requestersColumns}
                    itemProvider={new ArrayItemProvider<IExtensionActionViewModel>(this.props.extension.requests)}
                    singleClickActivation={false}
                />
                {resolver}
            </div>
        );
    }

    private renderActionTimeColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IExtensionActionViewModel>,
        tableItem: IExtensionActionViewModel
    ): JSX.Element => {
        let content: JSX.Element | undefined = undefined;
        const date = tableItem.date;

        if (date) {
            content = <Ago date={date} format={AgoFormat.Compact} />;
        }
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                {content}
            </SimpleTableCell>
        );
    };

    private renderUserMessage = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IExtensionActionViewModel>,
        tableItem: IExtensionActionViewModel
    ): JSX.Element => {
        return (
            <SimpleTableCell className="user-message" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                {tableItem.message}
            </SimpleTableCell>
        );
    };

    private renderUserNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ISimpleListCell>,
        tableItem: IExtensionActionViewModel
    ): JSX.Element => {
        let cell: ISimpleListCell = {
            iconProps: tableItem.userIconUrl
                ? {
                      render: (className: string) => {
                          const identity = {
                              displayName: tableItem.userName,
                              id: tableItem.userId,
                              imageUrl: tableItem.userIconUrl
                          };
                          return (
                              <Persona className={className} identity={identity} size={PersonaSize.size24} personaProvider={this.personaProvider} />
                          );
                      }
                  }
                : {
                      className: "vstsLogo",
                      iconName: "VSTSLogo"
                  },
            text: tableItem.userName,
            textClassName: "text-ellipsis"
        };

        return renderSimpleCellValue(columnIndex, tableColumn, cell);
    };
}
